import { Component, Input, ChangeDetectionStrategy } from '@angular/core';
import { CommonModule } from '@angular/common';
import { CellRenderer, CellRendererParams } from '../../core/interfaces/cell-renderer.interface';
import { DocumentNavigationComponent } from 'app/common/components/document-navigation/document-navigation.component';

/**
 * Document Cell Renderer Component
 *
 * Renders document type and number in format "TYPE - NUMBER".
 *
 * Features:
 * - Combines documentType and document fields
 * - Falls back to "-" for null/undefined values
 *
 * Usage in ColDef:
 * ```typescript
 * {
 *   colId: 'document',
 *   headerName: 'Nro. Doc.',
 *   cellRenderer: DocumentCellRendererComponent,
 *   sortable: true,
 *   valueGetter: (row) => row.document
 * }
 * ```
 *
 * Output examples:
 * - "FAC - 12345"
 * - "NCR - 98765"
 */
@Component({
  selector: 'app-document-cell-renderer',
  standalone: true,
  imports: [CommonModule, DocumentNavigationComponent],
  template: `
    <div class="flex flex-col">
      <app-document-navigation
        [documentType]="documentType"
        [documentSerial]="documentSerial"
        [displayText]="documentDisplay"
        [showIcon]="true"
      ></app-document-navigation>
    </div>
  `,
  styles: [`
    :host {
      display: block;
      width: 100%;
    }
  `],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class DocumentCellRendererComponent implements CellRenderer<unknown, unknown> {
  /**
   * Parameters from the grid containing the cell value and row data
   */
  @Input() params!: CellRendererParams<unknown, unknown>;

  private get rowData(): Record<string, string> {
    return (this.params?.data as Record<string, string>) || {};
  }

  get documentType(): string {
    return this.rowData['documentType'] || '';
  }

  get documentSerial(): string {
    return this.rowData['document'] || '';
  }

  /**
   * Get formatted document display
   */
  get documentDisplay(): string {
    return this.documentSerial || '-';
  }
}
